import React, { useState, useRef } from "react";
import { Table, Tag, Space, Tooltip } from "antd";
import { getTablePagination } from "../../../utils/tablePagination";
import { statusColors } from "../../../helpers/medicineKeys";
import { ExportOutlined } from "@ant-design/icons";
import EditIcon from "../../ui/EditIcon";
import ActionIconButton from "../../ui/ActionIconButton";
import { capitalizeFirstLetter, timeAgo } from "../../../helpers/utility";
import InvestigationModal from "../../PatientsIP/InvestigationsIp/InvestigationModal";
import InvestigationGallery from "../../PatientsIP/InvestigationsIp/InvestigationGallery/InvestigationGallery";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../hooks/usePageFillTableScroll";
import styles from "./Investigations.module.css";

const InvestigationTable = ({ data, refreshPage, page, setPage, loading }) => {
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const [editData, setEditData] = useState(null);
  const [open, setOpen] = useState(false);
  const [galleryModal, setGalleryModal] = useState(false);

  const tableData = data?.data?.filter((item) => !item?.is_initial) ?? [];
  const tableScroll = usePageFillTableScroll(
    tableWrapRef,
    isMobile,
    "default",
    tableData.length
  );

  const columns = [
    {
      title: "Investigation Name",
      dataIndex: "investigation_name",
      key: "investigation_name",
      width: isMobile ? 180 : undefined,
      ellipsis: isMobile,
      render: (value, record) => (value ? value : record?.name),
    },
    {
      title: "Patient Name",
      dataIndex: "patient_name",
      key: "patient_name",
      width: isMobile ? 140 : undefined,
      ellipsis: isMobile,
      render: (value) => capitalizeFirstLetter(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: isMobile ? 160 : undefined,
      render: (value, record) => (
        <>
          <Tag color={statusColors?.[value]}>{value?.replace("_", " ")}</Tag>
          {value === "Report_Delivered" ? (
            <Tooltip
              title={dayjs(record?.delivery_on).format("DD MMM, YYYY")}
            >
              {timeAgo(record?.delivery_on)}
            </Tooltip>
          ) : value === "Sample_Collected" ? (
            <Tooltip
              title={dayjs(record?.delivery_on).format("DD MMM, YYYY")}
            >
              {timeAgo(record?.sample_collected_on)}
            </Tooltip>
          ) : value === "Ordered" ? (
            <Tooltip title={dayjs(record?.created_at).format("DD MMM, YYYY")}>
              {timeAgo(record?.created_at)}
            </Tooltip>
          ) : null}
        </>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: isMobile ? 100 : undefined,
      fixed: isMobile ? "right" : undefined,
      render: (_, record) => (
        <Space>
          <ActionIconButton
            aria-label="Edit investigation"
            onClick={() => {
              setOpen(true);
              setEditData(record);
            }}
          >
            <EditIcon />
          </ActionIconButton>
          {record?.status === "Report_Delivered" ? (
            <ExportOutlined
              style={{ color: "green", fontSize: "18px" }}
              onClick={() => {
                setGalleryModal(true);
                setEditData(record);
              }}
            />
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div
        ref={tableWrapRef}
        className={`${styles.receptionInvestigationsTable} ui-table-fill-body`}
      >
        <Table
          className="no-vertical-borders"
          loading={loading}
          dataSource={tableData}
          columns={columns}
          rowKey="id"
          pagination={getTablePagination({
            current: page,
            pageSize: 50,
            total: data?.total_count ?? 0,
            onChange: (p) => setPage(p),
          })}
          scroll={tableScroll}
        />
      </div>

      {open && (
        <InvestigationModal
          refreshPage={refreshPage}
          open={open}
          setOpen={setOpen}
          editData={editData}
        />
      )}
      {galleryModal && (
        <InvestigationGallery
          open={galleryModal}
          setOpen={setGalleryModal}
          refreshPage={refreshPage}
          editData={editData}
        />
      )}
    </>
  );
};

export default InvestigationTable;
